import { MutationOptions, QueryClient, useMutation, useQueryClient } from "react-query"
import { useApiClient } from "../../api-client/api-client.hooks"
import { TApiTask, TApiTaskDetails } from "../../api-client/types"
import { TApiGenericError, TApiGenericResponse } from "../../timenotes-query/timenotes-query.types"
import { ICreateTaskParams, TApiTasksFilters } from "../../api-client/tasks-api"
import { useRefetchProjectSectionTasksAfterInjection } from "../../../features/project-board"
import { useInvalidateOtherProjectSections } from "../../project-sections/utils/use-invalidate-other-project-sections"

interface Args extends ICreateTaskParams {}

interface Result {
  task: TApiTask
}

interface UseCreateTaskMutationParams {
  projectId: string
  tasksFilters?: TApiTasksFilters
}

const useCreateTaskMutation = (params: UseCreateTaskMutationParams, options?: MutationOptions<any,any,any>) => {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  const {
    projectId,
    tasksFilters,
  } = params

  const refetchProjectSectionTasksAfterInjection = useRefetchProjectSectionTasksAfterInjection({
    projectId: projectId,
    filters: tasksFilters,
  })

  const invalidateOtherProjectSections = useInvalidateOtherProjectSections(projectId, tasksFilters)

  const query = useMutation<Result, TApiGenericError<TApiTask>, Args>(
    ['create-task'],
    async (args) => {
      const response = await apiClient.createTask(projectId, args)

      if (!response.ok) {
        throw response.errors
      }

      return {
        task: response.task,
      }
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // Refetch the board portion with the current sections only for the current filters
        // and invalidate all the other filters options to reflect the task properly
        if (tasksFilters) {
          refetchProjectSectionTasksAfterInjection.mutateAsync(variables.projectSectionId)
          invalidateOtherProjectSections()
        }
        if (options.onSuccess) options.onSuccess(data, variables, context)
      }
    },
  )

  return query
}

export default useCreateTaskMutation